import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Input, Row, Col, message, Switch, Card, Alert, Divider, Select } from 'antd';
import { MobileOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { motion } from "framer-motion";
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

const { Option } = Select;

const PhonePeGateway = ({ api, authToken, navigate }) => {
    const { Permisson } = useMyContext();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [merchantId, setMerchantId] = useState('');
    const [saltKey, setSaltKey] = useState('');
    const [saltIndex, setSaltIndex] = useState('');
    const [environment, setEnvironment] = useState('sandbox');
    const [callbackUrl, setCallbackUrl] = useState('');



    const fetchGatewaySettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`${api}payment-gateway/phonepe`, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                }
            });
            const data = response.data;
            
            // Handle the response according to your table structure with credentials object
            setIsEnabled(data.isEnabled || data.status === 'active' || false);
            
            // Extract from credentials object
            const credentials = data.credentials || {};
            setMerchantId(credentials.merchantId || '');
            setSaltKey(credentials.saltkey || '');
            setSaltIndex(credentials.saltIndex || '');
            setCallbackUrl(credentials.callbackUrl || '');
            setEnvironment(data.environment || 'sandbox');

            form.setFieldsValue({
                merchant_id: credentials.merchantId || '',
                salt_key: credentials.saltkey || '',
                salt_index: credentials.saltIndex || '',
                callback_url: credentials.callbackUrl || '',
                environment: data.environment || 'sandbox',
            });
        } catch (error) {
            console.error('Error fetching PhonePe settings:', error);
            // If no settings found, set default values
            form.setFieldsValue({
                environment: 'sandbox'
            });
        } finally {
            setLoading(false);
        }
    }, [api, authToken, form]);

    useEffect(() => {
        fetchGatewaySettings();
    }, [fetchGatewaySettings]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                gateway: 'phonepe',
                credentials: {
                    merchantId: values.merchant_id,
                    saltkey: values.salt_key,
                    saltIndex: values.salt_index,
                    callbackUrl: values.callback_url
                },
                environment: values.environment, // 'sandbox' or 'production'
                isEnabled: isEnabled,
                status: isEnabled ? 'active' : 'inactive'
            };

            const response = await apiClient.post(`${api}payment-gateway/phonepe`, payload, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('PhonePe settings updated successfully!');
                // Refresh the data after successful update
                fetchGatewaySettings();
            } else {
                message.error(response.data.message || 'Failed to update PhonePe settings');
            }
        } catch (error) {
            console.error('Error updating PhonePe settings:', error);
            message.error('Error updating PhonePe settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (checked) => {
        setIsEnabled(checked);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card bordered={false} className='shadow-none'>
                <div className="d-flex align-items-center mb-4">
                    <div>
                        <h2 className="mb-0">PhonePe Gateway</h2>
                        <p className="text-muted mb-0">Configure your PhonePe payment gateway settings</p>
                    </div>
                </div>

                <Alert
                    message="Important"
                    description="PhonePe is a leading digital payments platform in India. Ensure you have proper merchant onboarding completed before using in production."
                    type="info"
                    showIcon
                    className="mb-4"
                />

                <Row gutter={ROW_GUTTER}>
                    <Col xs={24} sm={24} md={17}>
                        <Card className="border border-light rounded-3">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Gateway Status</h5>
                                <Switch
                                    checked={isEnabled}
                                    onChange={handleSwitchChange}
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                />
                            </div>

                            <Divider />

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                className="mt-4"
                            >
                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Environment"
                                            name="environment"
                                            rules={[
                                                { required: true, message: 'Please select environment' }
                                            ]}
                                        >
                                            <Select
                                                placeholder="Select environment"
                                                value={environment}
                                                onChange={(value) => setEnvironment(value)}
                                                className="rounded-2"
                                            >
                                                <Option value="sandbox">Sandbox (UAT)</Option>
                                                <Option value="production">Production (Live)</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Merchant ID"
                                            name="merchant_id"
                                            rules={[
                                                { required: true, message: 'Please enter your Merchant ID' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="Enter Merchant ID"
                                                value={merchantId}
                                                onChange={(e) => setMerchantId(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Salt Key"
                                            name="salt_key"
                                            rules={[
                                                { required: true, message: 'Please enter your Salt Key' }
                                            ]}
                                        >
                                            <Input.Password
                                                placeholder="Enter Salt Key"
                                                value={saltKey}
                                                onChange={(e) => setSaltKey(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Salt Index"
                                            name="salt_index"
                                            rules={[
                                                { required: true, message: 'Please enter your Salt Index' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="Enter Salt Index (e.g., 1)"
                                                value={saltIndex}
                                                onChange={(e) => setSaltIndex(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Callback URL"
                                            name="callback_url"
                                            rules={[
                                                { required: true, message: 'Please enter callback URL' },
                                                { type: 'url', message: 'Please enter a valid URL' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="https://yourwebsite.com/callback"
                                                value={callbackUrl}
                                                onChange={(e) => setCallbackUrl(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                 <Form.Item className="mt-2">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        className='float-right'
                                        icon={<SaveOutlined />}
                                    >
                                        Save PhonePe Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} sm={24} md={7}>
                        <Card className="border border-light rounded-3 bg-light">
                            <h6 className="fw-bold mb-3">Setup Instructions</h6>
                            <div className="small text-muted">
                                <ol className="ps-3">
                                    <li className="mb-2">
                                        Sign up at <a href="https://business.phonepe.com" target="_blank" rel="noopener noreferrer">PhonePe Business</a>
                                    </li>
                                    <li className="mb-2">
                                        Complete merchant onboarding and KYC
                                    </li>
                                    <li className="mb-2">
                                        Get your Merchant ID, Salt Key, and Salt Index
                                    </li>
                                    <li className="mb-2">
                                        Configure callback URL for transaction updates
                                    </li>
                                    <li className="mb-2">
                                        Test payments in UAT environment first
                                    </li>
                                    <li className="mb-0">
                                        Go live after successful testing
                                    </li>
                                </ol>
                            </div>

                            <Alert
                                message="UAT Environment"
                                description="Use UAT environment for testing. Production requires live merchant account."
                                type="warning"
                                showIcon
                                className="mt-3"
                                size="small"
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>
        </motion.div>
    );
};

export default PhonePeGateway;
